import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Package, Loader2, Clock, CheckCircle2, Truck, Ban, RotateCcw, User, MapPin, Calendar, FileText, Receipt, ClipboardList, Eye } from "lucide-react";
import { toast } from "sonner";
import OrderDetailModal from "./OrderDetailModal";

const STATUSES = [
  { v: "paid",       l: "Payée",         cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  { v: "preparing",  l: "En préparation", cls: "bg-amber-100 text-amber-800",    Icon: Clock },
  { v: "ready",      l: "Prête",         cls: "bg-sky-100 text-sky-800",         Icon: Package },
  { v: "shipped",    l: "Expédiée",      cls: "bg-indigo-100 text-indigo-800",   Icon: Truck },
  { v: "collected",  l: "Retirée",       cls: "bg-slate-200 text-slate-800",     Icon: CheckCircle2 },
  { v: "completed",  l: "Terminée",      cls: "bg-slate-200 text-slate-800",     Icon: CheckCircle2 },
  { v: "cancelled",  l: "Annulée",       cls: "bg-red-100 text-red-700",         Icon: Ban },
  { v: "refunded",   l: "Remboursée",    cls: "bg-red-100 text-red-700",         Icon: RotateCcw },
];

export default function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [preparers, setPreparers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/admin/orders${params}`);
      setOrders(data);
      setPreparers((await api.get("/preparers?include_inactive=true")).data);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const stats = useMemo(() => {
    const out = { total: orders.length, revenue: 0 };
    orders.forEach(o => { out.revenue += (o.total_cents || 0); });
    return out;
  }, [orders]);

  const setStatus = async (oid, status) => {
    try {
      await api.put(`/admin/orders/${oid}/status`, { status });
      toast.success("Statut mis à jour"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const assign = async (oid, preparer_id) => {
    try {
      await api.put(`/admin/orders/${oid}/assign-preparer`, { preparer_id: preparer_id || null });
      toast.success("Préparateur assigné"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-5" data-testid="admin-orders-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">Boutique</div>
        <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Commandes</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Suivez les commandes payées, assignez un préparateur, faites évoluer le statut (en préparation → prête → retirée/expédiée).
        </p>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 border"><div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Commandes</div><div className="font-display text-3xl font-black text-[color:var(--brand-navy)] tabular-nums">{stats.total}</div></Card>
        <Card className="p-4 border"><div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">CA encaissé</div><div className="font-display text-3xl font-black text-[color:var(--brand-navy)] tabular-nums">{(stats.revenue / 100).toFixed(2)} €</div></Card>
        <Card className="p-4 border"><div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Préparateurs</div><div className="font-display text-3xl font-black text-[color:var(--brand-navy)] tabular-nums">{preparers.filter(p => p.active !== false).length}</div></Card>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Filtrer :</span>
        {[{ v: "all", l: "Toutes" }, ...STATUSES].map(opt => (
          <button
            key={opt.v}
            onClick={() => setFilter(opt.v)}
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all ${filter === opt.v ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "border-[hsl(var(--border))] hover:border-[color:var(--brand-navy)]"}`}
            data-testid={`orders-filter-${opt.v}`}
          >{opt.l}</button>
        ))}
      </div>

      {loading ? (
        <Card className="p-16 border flex items-center justify-center gap-2 text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</Card>
      ) : orders.length === 0 ? (
        <Card className="p-14 border text-center">
          <Package className="w-10 h-10 text-[hsl(var(--muted-foreground))]/30 mx-auto mb-3" />
          <div className="font-display font-bold text-[color:var(--brand-navy)]">Aucune commande</div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{filter === "all" ? "Les commandes apparaîtront ici dès un paiement réussi." : "Aucune commande pour ce statut."}</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map(o => {
            const st = STATUSES.find(x => x.v === o.status) || STATUSES[0];
            const itemCount = (o.line_items || []).reduce((s, l) => s + (l.qty || 0), 0);
            const prep = o.assigned_preparer;
            return (
              <Card key={o.id} className="p-4 border hover:shadow-md hover:border-[color:var(--brand-sky)]/40 transition" data-testid={`order-row-${o.id}`}>
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <button
                    type="button"
                    className="flex-1 min-w-0 text-left cursor-pointer"
                    onClick={() => setDetailId(o.id)}
                    data-testid={`order-open-${o.id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">#{o.id.slice(0, 8)}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${st.cls}`}><st.Icon className="w-3 h-3" />{st.l}</span>
                      {o.fulfillment_type === "pickup" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[color:var(--brand-sky)]/15 text-[color:var(--brand-sky)]"><MapPin className="w-3 h-3" />Click &amp; Collect</span>
                      )}
                    </div>
                    <div className="mt-1 text-sm font-semibold text-[color:var(--brand-navy)] line-clamp-1">
                      {(o.line_items || []).map(l => `${l.qty}× ${l.name}`).join(" · ") || "—"}
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                      {o.customer_email || "Invité"} · {itemCount} article{itemCount > 1 ? "s" : ""} · {new Date(o.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                    {o.fulfillment_type === "pickup" && o.pickup_slot_at && (
                      <div className="text-[11px] text-[color:var(--brand-navy)] mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Retrait {new Date(o.pickup_slot_at).toLocaleString("fr-FR", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} — {o.pickup_location_name}
                      </div>
                    )}
                  </button>
                  <div className="text-right shrink-0 md:mr-2">
                    <div className="font-bold text-[color:var(--brand-navy)] text-lg tabular-nums">{((o.total_cents || 0) / 100).toFixed(2)} {(o.currency || "eur").toUpperCase()}</div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0 md:min-w-[200px]">
                    <button
                      type="button"
                      onClick={() => setDetailId(o.id)}
                      className="inline-flex items-center justify-center gap-1 h-8 px-3 rounded-md bg-[color:var(--brand-navy)] text-white text-xs font-bold hover:bg-[color:var(--brand-sky)] transition"
                      data-testid={`order-detail-btn-${o.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Voir les détails
                    </button>
                    <Select value={o.status || "paid"} onValueChange={v => setStatus(o.id, v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={o.assigned_preparer_id || "none"} onValueChange={v => assign(o.id, v === "none" ? null : v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`order-preparer-${o.id}`}>
                        <SelectValue placeholder="Assigner…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Non assigné —</SelectItem>
                        {preparers.filter(p => p.active !== false).map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {prep && (
                      <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))]">
                        {prep.avatar_url ? (
                          <img src={prep.avatar_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                        ) : (
                          <User className="w-3 h-3" />
                        )}
                        <span className="truncate">{prep.first_name} {prep.last_name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 pt-1">
                      <DocButton oid={o.id} docType="invoice" label="Facture" Icon={Receipt} testid={`invoice-${o.id}`} existing={o.invoice_number} />
                      <DocButton oid={o.id} docType="delivery" label="BL" Icon={ClipboardList} testid={`bl-${o.id}`} />
                      <DocButton oid={o.id} docType="purchase" label="BC" Icon={FileText} testid={`bc-${o.id}`} />
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      {detailId && <OrderDetailModal orderId={detailId} onClose={() => { setDetailId(null); load(); }} />}
    </div>
  );
}

function DocButton({ oid, docType, label, Icon, testid, existing }) {
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    setLoading(true);
    try {
      // Generate (idempotent) then open PDF in a new tab
      await api.post(`/admin/orders/${oid}/invoice?doc_type=${docType}`);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${oid}/invoice/download?doc_type=${docType}`;
      const token = localStorage.getItem("kap_token") || "";
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error((await resp.json())?.detail || "Erreur");
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setLoading(false); }
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={loading}
      className="inline-flex items-center gap-1 h-7 px-2 rounded-md border text-[10px] font-bold hover:bg-[hsl(var(--muted))]/50 disabled:opacity-50"
      title={existing ? `${label} ${existing}` : `Générer ${label}`}
      data-testid={testid}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {label}
    </button>
  );
}
